import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import { isObject } from 'lodash-es';

import { EDITABLE_CELL, EDITABLE_HEADER } from '@shared/constants/ag-classes';
import { decimalAdd, decimalDivide, decimalMultiply } from '@shared/utils';
import { getCellClassForGroups } from '@shared/utils/ag-cell-class-for-groups';
import { Utils } from '@shared/utils/utils';

import {
  currencyFormatter,
  defaultHeaderClass,
  defaultMinWidth,
  defaultWidthForMoneyColumns,
  InMonthAdjustmentGridConfigContext,
  isDiscountRow,
  isTotalRow,
} from './ima-grid-config-context';
import { AgVendorEstimateCellComponent } from '../ag-vendor-estimate-cell.component';
import {
  ImaVendorEstimateColumnGroupHeaderComponent,
  ImaVendorEstimateColumnGroupHeaderParams,
} from './ima-vendor-estimate-column-group-header';
import { ImaTableGroups } from '../enum/ima-table-gropes';

export const imaVendorEstimateColumnGroup = (
  ctx: InMonthAdjustmentGridConfigContext
): ColGroupDef => {
  const {
    qcaComponent: { vendorEstimateColumnChecker },
  } = ctx;

  if (!vendorEstimateColumnChecker()) {
    return {
      children: [],
    };
  }

  return {
    children: getChildren(ctx),
    groupId: ImaTableGroups.VENDOR_ESTIMATE,
    headerClass: ['blue-light-marker header-marker'],
    headerGroupComponent: ImaVendorEstimateColumnGroupHeaderComponent,
    headerGroupComponentParams: <ImaVendorEstimateColumnGroupHeaderParams>{
      ctx,
    },
    headerName: 'Vendor Estimate',
    suppressStickyLabel: true,
  };
};

// eslint-disable-next-line max-lines-per-function
const getChildren = (ctx: InMonthAdjustmentGridConfigContext): ColDef[] => {
  const {
    qcaComponent: { editMode$, isVendorEstimatesLocked$, permissions },
  } = ctx;
  const editVendorEstimatePermission = permissions().editVendorEstimate;
  const editable = editVendorEstimatePermission && !isVendorEstimatesLocked$.value;

  return <ColDef[]>[
    {
      cellClass: (params) => ['text-right', getCellClassForGroups(params)],
      comparator: (a, b) => {
        const aVal = a?.vendor_estimate_percentage || 0;
        const bVal = b?.vendor_estimate_percentage || 0;

        return aVal === bVal ? 0 : aVal > bVal ? 1 : -1;
      },
      field: 'vendor_estimate_percentage',
      filter: 'agNumberColumnFilter',
      headerClass: defaultHeaderClass,
      headerName: 'Monthly % Complete',
      headerTooltip: 'Monthly % Complete',
      minWidth: defaultMinWidth,
      valueFormatter: ({ value }) => {
        return Utils.percentageFormatter(
          decimalDivide(value?.vendor_estimate_percentage || 0, 100),
          {
            maximumFractionDigits: 4,
          }
        );
      },
      // aggFunc: ImaAggTableService.aggregateMonthlyVendorPercentComplete,
      valueGetter: (params) => {
        const total_amount = params.data.total_amount || 0;
        const vendor_estimate_amount = params.data.vendor_estimate_amount || 0;
        const vendor_estimate_percentage = total_amount
          ? decimalMultiply(decimalDivide(vendor_estimate_amount, total_amount), 100, 6)
          : 0;

        return {
          toString: () => vendor_estimate_percentage.toString(),
          total_amount,
          vendor_estimate_amount,
          vendor_estimate_percentage,
        };
      },
      width: 135,
    },
    {
      cellClass: (params) => ['text-right', getCellClassForGroups(params)],
      field: 'vendor_estimate_unit',
      filter: 'agNumberColumnFilter',
      headerClass: defaultHeaderClass,
      headerName: 'Monthly Units',
      headerTooltip: 'Monthly Units',
      minWidth: defaultMinWidth,
      valueFormatter: ({ value }) => Utils.decimalFormatter(value),
      width: 115,
      // aggFunc: showUnitTotals$.getValue() ? 'sum' : '',
    },
    {
      cellClass: (params) => {
        const classes = ['text-right', ...getCellClassForGroups(params)];

        if (
          editable &&
          editMode$.value &&
          !isTotalRow(params) &&
          !!params.data?.canEditVendorEstimate
        ) {
          classes.push(EDITABLE_CELL);
        }

        return classes;
      },
      cellDataType: 'text',
      cellRenderer: AgVendorEstimateCellComponent,
      cellRendererParams: {
        dataPendoId: 'adjustments-vendor-estimate-expense',
        editMode$,
        editVendorEstimatePermission,
        isVendorEstimatesLocked$,
      },
      editable: (params) => {
        if (isTotalRow(params)) {
          return false;
        }

        return editable && editMode$.value && !!params.data?.canEditVendorEstimate;
      },
      field: 'vendor_estimate_amount',
      filter: 'agNumberColumnFilter',
      headerClass: () => {
        const classes = [...defaultHeaderClass];

        if (editable && editMode$.value) {
          classes.push(EDITABLE_HEADER);
        }

        return classes;
      },
      headerName: 'Monthly Expense ($)',
      headerTooltip: 'Monthly Expense ($)',
      minWidth: ctx.flags.showVendorEstimateAmount ? defaultMinWidth : 270,
      valueFormatter: currencyFormatter,
      valueParser: (params) => {
        return params.newValue === params.oldValue
          ? params.oldValue
          : Utils.castStringToNumber(params.newValue).toString();
      },
      width: ctx.flags.showVendorEstimateAmount ? defaultWidthForMoneyColumns : 270,
      // aggFunc: 'sum',
    },
    {
      cellClass: (params) => {
        const classes = ['text-right', ...getCellClassForGroups(params)];

        if (
          editable &&
          editMode$.value &&
          !isTotalRow(params) &&
          !!params.data?.canEditVendorEstimate
        ) {
          classes.push(EDITABLE_CELL);
        }

        return classes;
      },
      cellDataType: 'text',
      cellRenderer: AgVendorEstimateCellComponent,
      cellRendererParams: {
        dataPendoId: 'adjustments-vendor-estimate-expense-ltd',
        editMode$,
        editVendorEstimatePermission,
        isVendorEstimatesLocked$,
      },
      editable: (params) => {
        if (isTotalRow(params)) {
          return false;
        }

        return editable && editMode$.value && !!params.data?.canEditVendorEstimate;
      },
      field: 'vendor_estimate_amount_ltd',
      filter: 'agNumberColumnFilter',
      headerClass: () => {
        const classes = [...defaultHeaderClass];

        if (editable && editMode$.value) {
          classes.push(EDITABLE_HEADER);
        }

        return classes;
      },
      headerName: 'Expense LTD ($)',
      headerTooltip: 'Expense LTD ($)',
      hide: !ctx.flags.showVendorEstimateAmount,
      minWidth: defaultMinWidth,
      valueFormatter: (value) => currencyFormatter(value),
      valueParser: (params) => {
        return params.newValue === params.oldValue
          ? params.oldValue
          : Utils.castStringToNumber(params.newValue).toString();
      },
      width: defaultWidthForMoneyColumns,
      // aggFunc: 'sum',
    },
    {
      cellClass: (params) => {
        const classes = ['text-right', ...getCellClassForGroups(params)];

        if (
          editable &&
          editMode$.value &&
          !isTotalRow(params) &&
          !!params.data?.canEditVendorEstimate
        ) {
          classes.push(EDITABLE_CELL);
        }

        return classes;
      },
      cellRenderer: AgVendorEstimateCellComponent,
      cellRendererParams: {
        dataPendoId: 'adjustments-vendor_estimate_percent_ltd',
        editMode$,
        editVendorEstimatePermission,
        isVendorEstimatesLocked$,
      },
      comparator: (a, b) => {
        const aVal = a?.vendor_estimate_percent_ltd || 0;
        const bVal = b?.vendor_estimate_percent_ltd || 0;

        return aVal === bVal ? 0 : aVal > bVal ? 1 : -1;
      },
      editable: (params) => {
        if (isTotalRow(params)) {
          return false;
        }

        if (isDiscountRow(params)) {
          return false;
        }

        return editable && editMode$.value && !!params.data?.canEditVendorEstimate;
      },
      field: 'vendor_estimate_percent_ltd',
      filter: 'agNumberColumnFilter',
      headerClass: () => {
        const classes = [...defaultHeaderClass];

        if (editable && editMode$.value) {
          classes.push(EDITABLE_HEADER);
        }

        return classes;
      },
      headerName: '% Complete LTD',
      headerTooltip: '% Complete LTD',
      hide: !ctx.flags.showVendorEstimatePercent,
      minWidth: defaultMinWidth,
      valueFormatter: ({ value }) =>
        Utils.percentageFormatter(decimalDivide(value?.vendor_estimate_percent_ltd || 0, 100), {
          maximumFractionDigits: 4,
        }),
      // aggFunc: ImaAggTableService.aggregateVendorEstimatePercentLtd,
      valueGetter: (params) => {
        const total_amount = params.data.total_amount || 0;
        const actualsToDate = params.data.actuals_performed_to_date || 0;
        const vendor_estimate_amount = params.data.vendor_estimate_amount || 0;
        const previous_months_accruals = params.data.previous_months_accruals || 0;
        const vendor_estimate_percent_ltd = params.data.vendor_estimate_percent_ltd || 0;

        return {
          actualsToDate,
          previous_months_accruals,
          toString: () => {
            if (!total_amount) {
              return '0';
            }

            return decimalMultiply(
              decimalDivide(
                decimalAdd(
                  decimalAdd(actualsToDate || 0, vendor_estimate_amount),
                  previous_months_accruals
                ),
                total_amount
              ),
              100,
              6
            ).toString();
          },
          total_amount,
          vendor_estimate_amount,
          vendor_estimate_percent_ltd,
        };
      },
      valueParser: (params) => {
        return isObject(params.newValue) ||
          params.newValue === params.oldValue.vendor_estimate_percent_ltd
          ? params.oldValue.vendor_estimate_percent_ltd
          : Utils.castStringToNumber(params.newValue).toString();
      },
      width: 140,
    },
  ];
};
